/**
 * Security center (Phase J).
 *
 * `rotateAuthorizationKey` returns HTTP 503 until `OCPP_CSMS_ROTATION_READY=1`
 * is set on the backend. The Security tab should disable the button with
 * a runbook tooltip in that case.
 *
 * `rotateAuthorizationKey` and `escalateSecurityProfile` return HTTP 202
 * with `{ op_id, state }`; the UI should switch to polling
 * `getSecurityOverview` every 10–15s until the op terminates.
 */

import { enc, ocppRequest } from './ocppApi';
import type {
  DeleteCertificateBody,
  EscalateBody,
  InstallCertificateBody,
  InstallCertificateResponse,
  ListCertificatesResponse,
  ListSecurityEventsResponse,
  OpAcceptedResponse,
  ProvisionBody,
  PushSignedCertificateBody,
  RotateAuthorizationKeyBody,
  SecurityOverview,
  SignCsrBody,
} from '../models';

export const getSecurityOverview = (chargerId: string) =>
  ocppRequest<SecurityOverview>({
    method: 'GET',
    path: `security/${enc(chargerId)}`,
  });

export const listSecurityEvents = (chargerId: string, limit?: number) =>
  ocppRequest<ListSecurityEventsResponse>({
    method: 'GET',
    path: `security/${enc(chargerId)}/events`,
    query: { limit },
  });

export const provisionCharger = (chargerId: string, body: ProvisionBody = {}) =>
  ocppRequest<{ success: boolean; message?: string }>({
    method: 'POST',
    path: `security/${enc(chargerId)}/provision`,
    body,
  });

export const rotateAuthorizationKey = (chargerId: string, body: RotateAuthorizationKeyBody) =>
  ocppRequest<OpAcceptedResponse>({
    method: 'POST',
    path: `security/${enc(chargerId)}/rotate`,
    body,
  });

export const escalateSecurityProfile = (chargerId: string, body: EscalateBody) =>
  ocppRequest<OpAcceptedResponse>({
    method: 'POST',
    path: `security/${enc(chargerId)}/escalate`,
    body,
  });

export const listCertificates = (
  chargerId: string,
  certificate_type?: InstallCertificateBody['certificate_type']
) =>
  ocppRequest<ListCertificatesResponse>({
    method: 'GET',
    path: `security/${enc(chargerId)}/certificates`,
    query: { certificate_type },
  });

export const installCertificate = (chargerId: string, body: InstallCertificateBody) =>
  ocppRequest<InstallCertificateResponse>({
    method: 'POST',
    path: `security/${enc(chargerId)}/certificates`,
    body,
  });

export const deleteCertificate = (chargerId: string, body: DeleteCertificateBody) =>
  ocppRequest<{ success: boolean; status: string }>({
    method: 'DELETE',
    path: `security/${enc(chargerId)}/certificates`,
    body,
  });

export const requestCertificateSigning = (chargerId: string, body: SignCsrBody) =>
  ocppRequest<{ success: boolean; status: string }>({
    method: 'POST',
    path: `security/${enc(chargerId)}/certificates/sign`,
    body,
  });

export const pushSignedCertificate = (chargerId: string, body: PushSignedCertificateBody) =>
  ocppRequest<{ success: boolean; status: string }>({
    method: 'POST',
    path: `security/${enc(chargerId)}/certificates/signed`,
    body,
  });
